import * as React from 'react';
import { Checkbox, Space, Row, Col, Typography, Button, Input, Form, Select, Alert } from 'antd';
import styleLogin from './login.module.scss';
import { CloseCircleFilled } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppInfo, LoginInput } from '@bit-box/common';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { useSessionStorage } from '../hooks/use-session-storage';
import { PAGE_URL_REGISTER, PAGE_URL_PASSWORD_RESET, COPY_RIGHT } from '../common/constant';
import type { InputRef } from 'antd';
import type { BaseSelectRef } from 'rc-select';
import useAutoFocus from '../hooks/use-autofocus';
import { migrateStorage } from '../common/migration';
import range from 'lodash-es/range';
import { useConnectionContext } from '../providers/connection-provider';
import { getApiUrl } from '../common/utils';
import { openWindow } from '../common/electron';

const { Text } = Typography;

const DEFAULT_LOGIN_INPUT: LoginInput = {
  botId: 0,
  email: '',
  password: '',
  rememberEmail: false,
  rememberPassword: false,
};

/**
 * 2023-10-17 buruss Trpc 사용하지 않기로 함
 * 원래는 _app.tsx 맨 밑에서 trpcNext.withTRPC로 감싸서 모든 페이지에서 trpc를 사용했었음.
 * 하지만 이렇게 하면 로그인 페이지(index.tsx)에서 아직 봇 번호가 결정되지 않은 상태에서
 * trpc용 websocket이 3000 포트로 연결되어서
 * 봇 선택하고 로그인한 후에도 계속 3000 포트로 연결되는 문제가 있음.
 * 그래서 로그인 페이지(index.tsx)에서는 trpc를 아예 사용하지 않기로 함.
 * 대신, trpc로 감싸주는 역할은 default-layout.tsx 및 bot-layout.tsx 에서 함.
 * 로그인 페이지에서는 EmptyLayout을 사용하여 trpc를 사용하지 않음.
 */
const Login = () => {
  // Electron인 경우 botId는 명령줄 인자로 받은 값으로 고정되어야 하므로 이 Provider에서 전달받은 앱 정보를 사용함.
  const { electronAppInfo } = useConnectionContext();
  const isElectron = !!electronAppInfo;
  // console.log('electronAppInfo', electronAppInfo);

  // Electron인 경우에는 명령줄로 받은 BotId로 고정
  const [botId, setBotId] = useSessionStorage<number>('botId', electronAppInfo?.botId || 0);
  const [frm] = Form.useForm<LoginInput>();
  const router = useRouter();

  const [loginError, setLoginError] = useState('');
  const [loginIsLoading, setLoginIsLoading] = useState(false);
  const autofocusRef = useAutoFocus();

  // const [loginInfo, setLoginInfo] = useState<LoginInput>({
  //   botId,
  //   email: '',
  //   password: '',
  //   rememberEmail: false,
  //   rememberPassword: false,
  // });

  // ElectronApp인 경우 이미 가지고 있는 AppInfo를 그대로 유지함.
  const [appInfo, setAppInfo] = useState<AppInfo | undefined>(electronAppInfo);

  /**
   * 인자로 주어진 botId를 이용하여 API URL을 생성한다.
   */
  // const getApiUrlFromBotId = useCallback(
  //   (botId: number) => {
  //     return getApiUrl(isElectron ? { ...electronAppInfo, botId } : undefined);
  //   },
  //   [electronAppInfo],
  // );

  // BotId 바뀌면 해당 봇에서 다시 저장한 아이디/비번 불러오기
  const getRememberInfo = useCallback(async () => {
    if (!botId) {
      return;
    }

    try {
      const res = await fetch(`${getApiUrl(electronAppInfo)}/app.getRememberInfo`, {
        method: 'GET',
        credentials: 'include',
        body: undefined,
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(res => res.json());

      // console.log('result getRememberInfo', res);

      const newLoginInfo: LoginInput = res.result?.data?.json;
      if (!newLoginInfo) {
        return;
      }
      console.log('result loginInfo', newLoginInfo);

      // setLoginInfo(newLoginInfo);
      if (newLoginInfo.email) {
        frm.setFieldValue('email', newLoginInfo.email);
        frm.setFieldValue('rememberEmail', newLoginInfo.rememberEmail);
        frm.setFieldValue('password', newLoginInfo.password);
        frm.setFieldValue('rememberPassword', newLoginInfo.rememberPassword);
      }
    } catch (e) {
      console.error(e);
    }
  }, [botId, electronAppInfo, frm]);

  // botId 바뀌면 해당 봇에서 app 정보 다시 불러오기
  const getAppInfo = useCallback(async () => {
    // Electron인 경우에는 이미 window.electronAPI.getAppInfo()로 가져온 정보가 있으므로 아래 요청이 불필요함.
    if (isElectron) {
      return;
    }
    if (!botId) {
      return;
    }
    try {
      const res = await fetch(`${getApiUrl(electronAppInfo)}/app.info`, {
        method: 'GET',
        credentials: 'include',
        body: undefined,
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(res => res.json());
      setAppInfo(res.result?.data?.json);

      console.log('result loginInfo', res.result?.data?.json);
    } catch (e) {
      console.error(e);
    }
  }, [botId, electronAppInfo, isElectron]);

  useEffect(() => {
    // console.log(`call fetchRememberInfo`);
    getRememberInfo();
    getAppInfo();
  }, [getRememberInfo, getAppInfo]);

  const handleLoginClick = useCallback(
    async (input: LoginInput) => {
      // const input = await frmLogin.validateFields();
      // console.log('handleLoginClick input', input);

      setLoginIsLoading(true);

      try {
        const result = await fetch(`${getApiUrl(electronAppInfo)}/app.login`, {
          method: 'POST',
          body: JSON.stringify({ json: input }),
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }).then(res => res.json());

        // console.log('login result', result);
        if (result.result?.data?.json?.userId) {
          // localStorage 마이그레이션
          migrateStorage();
          console.log('login', '이용권 선택 화면으로 이동');
          await router.push('/license-select');
        } else if (result.error?.json?.message) {
          throw new Error(result.error.json.message);
        }
      } catch (err) {
        if (err instanceof Error && err.message) {
          setLoginError(err.message);
        }
      } finally {
        setLoginIsLoading(false);
      }
    },
    [router, electronAppInfo],
  );

  // console.log('index', { botId, isElectron: isElectron });

  const botValueLabels = useMemo(
    () => [
      { value: 0, label: 'Bot을 선택해주세요.' },
      // 예를 들어 MAX_BOTS 상수가 5이면 1,2,3,4,5 까지 배열 생성 후 목록 생성함
      ...range(1, Number(process.env.NEXT_PUBLIC_API_MAX_BOTS || 1) + 1).map(num => ({
        value: num,
        label: `${num}번`,
      })),
    ],
    [],
  );
  const handleCloseClick = () => {
    if (isElectron) {
      // 닫아야해,,,
    }
  };

  const handleBotIdChange = (value: number) => {
    setBotId(value);
    // setLoginInfo({ ...loginInfo, botId: value });
  };

  // 프록시 오류 정보를 sessionStorage에 저장 (로그인 과정에서 유지하기 위함)
  useEffect(() => {
    const proxyErrorParam: string | undefined =
      typeof router.query.proxyError === 'string' ? router.query.proxyError : undefined;
    if (proxyErrorParam) {
      sessionStorage.setItem('pendingProxyError', proxyErrorParam);
      console.log('프록시 오류 정보를 sessionStorage에 저장:', proxyErrorParam);
    }
  }, [router.query.proxyError]);

  // const handleRememberEmailChange = (e: CheckboxChangeEvent) => {
  // console.log(`RememberEmail checked = ${e.target.checked}`);
  // if (isElectron) {
  //   const newLoginInfo: LoginInput = {
  //     ...loginInfo,
  //     rememberEmail: e.target.checked,
  //   };
  //   setLoginInfo(newLoginInfo);
  // }
  // };

  // const handleRememberPasswordChange = (e: CheckboxChangeEvent) => {
  // console.log(`RememberPassword checked = ${e.target.checked}`);
  // if (isElectron) {
  //   const newLoginInfo: LoginInput = {
  //     ...loginInfo,
  //     rememberPassword: e.target.checked,
  //   };
  //   setLoginInfo(newLoginInfo);
  // }
  // };

  // useEffect(() => {
  //   frm.setFieldsValue({
  //     email: loginInfo.email,
  //   });
  // }, [frm, loginInfo.email]);

  // useEffect(() => {
  //   frm.setFieldsValue({
  //     password: loginInfo.password,
  //   });
  // }, [frm, loginInfo.password]);

  // useEffect(() => {
  //   frm.setFieldsValue({
  //     rememberEmail: loginInfo.rememberEmail,
  //   });
  // }, [frm, loginInfo.rememberEmail]);

  // useEffect(() => {
  //   frm.setFieldsValue({
  //     rememberPassword: loginInfo.rememberPassword,
  //   });
  // }, [frm, loginInfo.rememberPassword]);

  const handleRegisterClick = () => {
    openWindow(PAGE_URL_REGISTER);
  };

  const handlePasswordResetClick = () => {
    openWindow(PAGE_URL_PASSWORD_RESET);
  };

  return (
    <>
      <Head>
        <title>로그인</title>
        <title>{(appInfo?.appName ? `${appInfo.appName} ${botId}번 - v${appInfo.appVersion} ` : '') + `로그인`}</title>
      </Head>
      <div className={styleLogin.loginBackground}>
        <Form
          id="loginForm"
          form={frm}
          name="frmLogin"
          initialValues={{ ...DEFAULT_LOGIN_INPUT, botId }}
          onFinish={handleLoginClick}
          style={{ padding: '0px 30px' }}
        >
          <div className={styleLogin.loginForm}>
            <div>
              {/* 종료 버튼은 사용하지 않기로 하여 숨김 */}
              <Row
                justify="end"
                style={{
                  visibility: 'hidden',
                }}
              >
                <Col>
                  <CloseCircleFilled
                    onClick={handleCloseClick}
                    title="종료"
                    style={{ fontSize: '1.2rem', color: 'var(--accentColor)' }}
                  />
                </Col>
              </Row>
              <Row justify="end" style={{ marginTop: '5px' }}>
                <Col>
                  <Text className="pr-2" type="secondary">
                    {appInfo?.appVersion ? `Ver. ${appInfo?.appVersion}` : ''}
                  </Text>
                </Col>
              </Row>
            </div>

            <div className={isElectron ? styleLogin.loginContentElectron : styleLogin.loginContent}>
              <Row gutter={16}>
                <Col span={11}>
                  <Space size={1} orientation="vertical" style={{ display: 'flex', width: '168px' }}>
                    {/* 봇 선택 */}
                    <Form.Item
                      className={`${styleLogin.formItem} form-item-err`}
                      name="botId"
                      rules={[{ required: true, type: 'number', min: 1, message: 'Bot을 선택해주세요.' }]}
                      style={{ display: isElectron ? 'none' : 'block' }}
                    >
                      <Select
                        className="text-center"
                        options={botValueLabels}
                        value={botId}
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                        ref={isElectron ? null : (autofocusRef as React.Ref<BaseSelectRef>)}
                        onChange={handleBotIdChange}
                      />
                    </Form.Item>
                    {/* 아이디 */}
                    <Form.Item
                      className={`${styleLogin.formItem} form-item-err`}
                      name="email"
                      rules={[{ required: true, message: '아이디를 입력해주세요.' }]}
                    >
                      <Input
                        type="email"
                        // prefix={<UserOutlined className={styleLogin.inputIcon} />}
                        placeholder="ID (E-Mail)"
                        style={{ height: '32px' }}
                        // value={rememberEmail}
                        autoComplete="email"
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                        ref={isElectron ? (autofocusRef as React.Ref<InputRef>) : null}
                      />
                    </Form.Item>
                    {/* 비밀번호 */}
                    <Form.Item
                      className={`${styleLogin.formItem} form-item-err`}
                      name="password"
                      rules={[{ required: true, message: '비밀번호를 입력해주세요.' }]}
                    >
                      <Input.Password
                        style={{ height: '32px' }}
                        placeholder="PW"
                        autoComplete="current-password"
                        // value={rememberPassword}
                      />
                    </Form.Item>
                  </Space>
                </Col>
                <Col span={6}>
                  <Button
                    className={`loginButton`}
                    loading={loginIsLoading}
                    disabled={loginIsLoading}
                    htmlType="submit"
                    form="loginForm"
                    size="large"
                    style={{ height: '100%', overflow: 'hidden' }}
                    block
                  >
                    {loginIsLoading ? <br /> : null}
                    Log in
                  </Button>
                </Col>
              </Row>
              <Row>
                {loginError ? <Alert title="아이디 또는 비밀번호가 일치하지 않습니다." type="error" showIcon /> : null}
              </Row>
            </div>

            <div className={styleLogin.loginBottom}>
              <Row id="bottom">
                <Col span={24} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Space>
                    {/* {isElectron ? (
                      <a target="_blank" href={PageURLRegister}>
                        회원가입
                      </a>
                    ) : (
                      <Button className={styleLogin.bottomBtn} onClick={handleRegisterClick}>
                        회원 가입
                      </Button>
                    )} */}
                    <Button className={styleLogin.bottomBtn} onClick={handleRegisterClick}>
                      회원 가입
                    </Button>
                    <Button className={styleLogin.bottomBtn} onClick={handlePasswordResetClick}>
                      PW 찾기
                    </Button>
                  </Space>

                  <div
                    className="checkRemember"
                    style={{
                      flexGrow: 1,
                      textAlign: 'right',
                      // visibility: 'visible',
                      visibility: isElectron ? 'visible' : 'hidden',
                    }}
                  >
                    <Space size={10}>
                      <Form.Item name="rememberEmail" valuePropName="checked">
                        <Checkbox className={styleLogin.checkRemember} /*  onChange={handleRememberEmailChange} */>
                          ID 저장
                        </Checkbox>
                      </Form.Item>
                      <Form.Item name="rememberPassword" valuePropName="checked">
                        <Checkbox className={styleLogin.checkRemember} /*  onChange={handleRememberPasswordChange} */>
                          PW 저장
                        </Checkbox>
                      </Form.Item>
                    </Space>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Form>
      </div>
      <div className={styleLogin.copyright}>
        <p>{COPY_RIGHT}</p>
      </div>
    </>
  );
};

Login.layout = 'EmptyLayout';
export default Login;
